import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import { Upload, Trash2, Plus, Image, X, Eye, GripVertical, Pencil, Star } from 'lucide-react';

const GalleryTab = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: 'residential',
    product: 'roller-shades',
    description: '',
    file: null
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('category', uploadForm.category);
      formData.append('product', uploadForm.product);
      formData.append('description', uploadForm.description);

      await axios.post(`${API_URL}/admin/gallery/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Project uploaded successfully!');
      setShowUploadDialog(false);
      setUploadForm({ title: '', category: 'residential', product: 'roller-shades', description: '', file: null });
      fetchProjects();
    } catch (error) {
      toast.error('Failed to upload project');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/gallery/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleSetFeatured = async (projectId) => {
    try {
      await axios.post(`${API_URL}/admin/gallery/${projectId}/featured`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Featured image updated');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newProjects = [...projects];
    const item = newProjects[draggedItem];
    newProjects.splice(draggedItem, 1);
    newProjects.splice(index, 0, item);
    
    setProjects(newProjects);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (draggedItem === null) return;
    
    // Save new order to backend
    try {
      await axios.post(`${API_URL}/admin/gallery/reorder`, 
        { order: projects.map(p => p.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Gallery order saved');
    } catch (error) {
      console.error('Failed to save order:', error);
      // Order is already visually updated, so don't show error for silent fail
    }
    setDraggedItem(null);
  };

  const handleUpdateProject = async (projectId, updates) => {
    try {
      await axios.put(`${API_URL}/admin/gallery/${projectId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project updated');
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Gallery Projects</h2>
          <p className="text-neutral-500 text-sm">Drag to reorder • Click star to feature • Eye to preview</p>
        </div>
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-gallery-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Gallery Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-medium">Project Title *</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="gallery-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                    className="w-full mt-1 input-form"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Product Type</Label>
                  <select
                    value={uploadForm.product}
                    onChange={(e) => setUploadForm({ ...uploadForm, product: e.target.value })}
                    className="w-full mt-1 input-form"
                  >
                    <option value="roller-shades">Roller Shades</option>
                    <option value="motorized-drapes">Motorized Drapes</option>
                    <option value="roman-shades">Roman Shades</option>
                    <option value="cellular-shades">Cellular Shades</option>
                    <option value="solar-shades">Solar Shades</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Image/Video *</Label>
                <div className="mt-1 border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    className="hidden"
                    id="gallery-file-upload"
                  />
                  <label htmlFor="gallery-file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-neutral-400 mb-2" />
                    <p className="text-sm text-neutral-600">
                      {uploadForm.file ? uploadForm.file.name : 'Click to upload image or video'}
                    </p>
                  </label>
                </div>
              </div>
              <Button type="submit" disabled={uploading} className="w-full btn-primary">
                {uploading ? 'Uploading...' : 'Upload Project'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <Image className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
          <p className="text-neutral-600">No gallery projects yet</p>
          <p className="text-sm text-neutral-500">Upload your first project to get started</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {projects.map((project, index) => (
            <div 
              key={project.id} 
              className={`border border-neutral-200 rounded-lg overflow-hidden group relative ${
                project.is_featured ? 'ring-2 ring-amber-500' : ''
              } ${draggedItem === index ? 'opacity-50' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              {/* Drag handle indicator */}
              <div className="absolute top-2 left-2 z-10 bg-white/80 rounded p-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical className="w-4 h-4 text-neutral-500" />
              </div>
              
              {/* Featured badge */}
              {project.is_featured && (
                <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" /> Featured
                </div>
              )}
              
              {/* AI Generated badge */}
              {project.is_ai_generated && (
                <div className="absolute top-2 right-2 z-10 bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                  AI
                </div>
              )}
              
              <div className="aspect-[4/3] bg-neutral-100 relative">
                {project.image ? (
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                ) : project.video_url ? (
                  <video src={project.video_url} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-neutral-300" />
                  </div>
                )}
                
                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {/* Preview button */}
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    onClick={() => setPreviewImage({ url: project.image, title: project.title })}
                    title="View full size"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  
                  {/* Feature button */}
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => handleSetFeatured(project.id)}
                    title={project.is_featured ? 'Remove from featured' : 'Set as featured'}
                    className={project.is_featured ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
                  >
                    <Star className={`w-4 h-4 ${project.is_featured ? 'fill-current' : ''}`} />
                  </Button>
                  
                  {/* Edit button */}
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => setEditingProject(project)}
                    title="Edit project"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  
                  {/* Delete button */}
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(project.id)}
                    title="Delete project"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <p className="font-medium text-sm truncate">{project.title}</p>
                <p className="text-xs text-neutral-500 capitalize">{project.category} • {project.product?.replace('-', ' ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-black">
          <DialogHeader className="sr-only">
            <DialogTitle>{previewImage?.title || 'Image Preview'}</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full p-2"
            >
              <X className="w-5 h-5" />
            </Button>
            {previewImage && (
              <img 
                src={previewImage.url} 
                alt={previewImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
          </div>
          <div className="bg-black/90 text-white p-4 text-center">
            <p className="text-sm">{previewImage?.title}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={!!editingProject} onOpenChange={() => setEditingProject(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Gallery Project</DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-medium">Title</Label>
                <Input
                  value={editingProject.title || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <select
                    value={editingProject.category || 'residential'}
                    onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                    className="w-full mt-1 input-form"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Product Type</Label>
                  <select
                    value={editingProject.product || 'roller-shades'}
                    onChange={(e) => setEditingProject({ ...editingProject, product: e.target.value })}
                    className="w-full mt-1 input-form"
                  >
                    <option value="roller-shades">Roller Shades</option>
                    <option value="motorized-drapes">Motorized Drapes</option>
                    <option value="roman-shades">Roman Shades</option>
                    <option value="cellular-shades">Cellular Shades</option>
                    <option value="solar-shades">Solar Shades</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  value={editingProject.description || ''}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingProject(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleUpdateProject(editingProject.id, {
                    title: editingProject.title,
                    category: editingProject.category,
                    product: editingProject.product,
                    description: editingProject.description
                  })}
                  className="flex-1 btn-primary"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// NOTE: FabricColorsTab is now imported from ../components/admin/FabricColorsTab.jsx

// Consultations Tab


export default GalleryTab;
